/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { ReactNode } from 'react';
import { t } from '@superset-ui/core';
import { InfoTooltipWithTrigger } from '@superset-ui/chart-controls';
import ColorPickerControl from 'src/explore/components/controls/ColorPickerControl';
import { FormItem } from 'src/components/Form';
import Select from 'src/components/Select/Select';
import { Input, InputNumber } from 'src/components/Input';
import { Switch } from 'src/components/Switch';
import { ConditionalFormattingConfig } from './types';
import {
  ControlRow,
  ControlRowControl,
  ControlRowLabel,
  FieldBlock,
  FieldPair,
  FieldTriple,
  PreviewCaption,
  PreviewCell,
  PreviewSample,
  PreviewStrip,
  PreviewSwatch,
} from './FormattingPopoverLayout';
import {
  displayTypeOptions,
  iconSetTypeOptions,
  integerFieldRules,
  renderOperatorFields,
  rulesRequired,
  shouldOperatorFieldsUpdate,
} from './formattingFormShared';

type ColumnOption = { label: string; value: string };

function LabelWithTip({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <span>
      {label}{' '}
      <InfoTooltipWithTrigger placement="top" label={label} tooltip={tooltip} />
    </span>
  );
}

function ToggleField({ name, label }: { name: string; label: ReactNode }) {
  return (
    <ControlRow>
      <ControlRowLabel>{label}</ControlRowLabel>
      <ControlRowControl>
        <FormItem name={name} valuePropName="checked" noStyle>
          <Switch size="small" />
        </FormItem>
      </ControlRowControl>
    </ControlRow>
  );
}

/** ① 规则：列 + 条件 + 过滤 0 值 */
export function RuleSectionFields({ columns }: { columns: ColumnOption[] }) {
  return (
    <>
      <FieldBlock>
        <FormItem name="column" label={t('Column')} rules={rulesRequired}>
          <Select ariaLabel={t('Select column')} options={columns} />
        </FormItem>
      </FieldBlock>

      <FieldBlock>
        <FormItem noStyle shouldUpdate={shouldOperatorFieldsUpdate}>
          {renderOperatorFields}
        </FormItem>
      </FieldBlock>

      <ToggleField
        name="filterZero"
        label={
          <LabelWithTip
            label={t('过滤0值')}
            tooltip={t('勾选后，值为0的数据不参与条件判定和差值计算')}
          />
        }
      />
    </>
  );
}

/** ② 样式：颜色 + 预览；展现方式可选 */
export function StyleSectionFields({
  colorScheme,
  config,
  showDisplayOptions = false,
}: {
  colorScheme: { label: string; value: string }[];
  config?: ConditionalFormattingConfig;
  showDisplayOptions?: boolean;
}) {
  return (
    <>
      <FieldBlock>
        <FormItem
          name="colorScheme"
          label={t('Color scheme')}
          rules={rulesRequired}
        >
          <Select ariaLabel={t('Color scheme')} options={colorScheme} />
        </FormItem>
      </FieldBlock>

      <FormItem
        noStyle
        shouldUpdate={(prev, cur) => prev.colorScheme !== cur.colorScheme}
      >
        {({ getFieldValue }) => {
          const selectedColor = getFieldValue('colorScheme');
          if (selectedColor === 'custom') {
            return (
              <FieldBlock>
                <FormItem
                  name="customColor"
                  label={t('自定义颜色')}
                  rules={rulesRequired}
                  initialValue={
                    config?.customColor || {
                      r: 82,
                      g: 196,
                      b: 26,
                      a: 100,
                    }
                  }
                >
                  <ColorPickerControl disableAlpha />
                </FormItem>
              </FieldBlock>
            );
          }
          return (
            <PreviewStrip>
              <PreviewCell>
                <PreviewCaption>{t('颜色预览')}</PreviewCaption>
                <PreviewSwatch $color={selectedColor} />
              </PreviewCell>
              <PreviewCell>
                <PreviewCaption>{t('字体预览')}</PreviewCaption>
                <PreviewSample $color={selectedColor}>
                  {t('1234.12%')}
                </PreviewSample>
              </PreviewCell>
            </PreviewStrip>
          );
        }}
      </FormItem>

      {showDisplayOptions && (
        <>
          <ToggleField
            name="enableColorScale"
            label={
              <LabelWithTip
                label={t('启用色阶')}
                tooltip={t(
                  '勾选后，根据数值在最小值和最大值之间的位置，应用渐变色阶。颜色从配置的最小值颜色过渡到最大值颜色，直观展示数据分布。',
                )}
              />
            }
          />

          <FieldBlock>
            <FormItem
              name="displayType"
              label={
                <LabelWithTip
                  label={t('选择展现方式')}
                  tooltip={t('选择一种数据展现方式，使数据更加直观。')}
                />
              }
            >
              <Select
                ariaLabel={t('选择展现方式')}
                options={displayTypeOptions}
              />
            </FormItem>
          </FieldBlock>

          <DisplayTypeSpecificFields />
        </>
      )}
    </>
  );
}

function DisplayTypeSpecificFields() {
  return (
    <FormItem
      noStyle
      shouldUpdate={(prev, cur) => prev.displayType !== cur.displayType}
    >
      {({ getFieldValue }) => {
        const type = getFieldValue('displayType');

        if (type === 'progressBar') {
          return (
            <FieldPair>
              <FormItem name="progressBarStyle" label={t('样式')}>
                <Select
                  ariaLabel={t('进度条样式')}
                  options={[
                    { label: t('经典'), value: 'classic' },
                    { label: t('条纹'), value: 'striped' },
                  ]}
                />
              </FormItem>
              <FormItem
                name="progressBarWidth"
                label={t('宽度')}
                rules={integerFieldRules(0, 30)}
              >
                <InputNumber min={0} max={30} style={{ width: '100%' }} />
              </FormItem>
            </FieldPair>
          );
        }

        if (type === 'miniBarChart') {
          return (
            <>
              <FieldBlock>
                <FormItem name="miniBarChartStyle" label={t('样式')}>
                  <Select
                    ariaLabel={t('柱状图样式')}
                    options={[
                      { label: t('垂直'), value: 'vertical' },
                      { label: t('水平'), value: 'horizontal' },
                    ]}
                  />
                </FormItem>
              </FieldBlock>
              <FieldPair>
                <FormItem
                  name="miniBarChartWidth"
                  label={t('宽度')}
                  rules={integerFieldRules(8, 48)}
                >
                  <InputNumber min={8} max={48} style={{ width: '100%' }} />
                </FormItem>
                <FormItem
                  name="miniBarChartHeight"
                  label={t('高度')}
                  rules={integerFieldRules(16, 64)}
                >
                  <InputNumber min={16} max={64} style={{ width: '100%' }} />
                </FormItem>
              </FieldPair>
            </>
          );
        }

        if (type === 'donutProgress') {
          return (
            <FieldPair>
              <FormItem
                name="donutSize"
                label={t('直径')}
                rules={integerFieldRules(12, 48)}
              >
                <InputNumber min={12} max={48} style={{ width: '100%' }} />
              </FormItem>
              <FormItem
                name="donutThickness"
                label={t('厚度')}
                rules={integerFieldRules(2, 12)}
              >
                <InputNumber min={2} max={12} style={{ width: '100%' }} />
              </FormItem>
            </FieldPair>
          );
        }

        if (type === 'iconSet') {
          return (
            <FieldBlock>
              <FormItem name="iconSetType" label={t('图标类型')}>
                <Select
                  ariaLabel={t('图标集类型')}
                  options={iconSetTypeOptions}
                />
              </FormItem>
            </FieldBlock>
          );
        }

        if (type === 'miniPieChart') {
          return (
            <FieldBlock>
              <FormItem
                name="miniPieChartSize"
                label={t('直径')}
                rules={integerFieldRules(12, 48)}
              >
                <InputNumber min={12} max={48} style={{ width: '100%' }} />
              </FormItem>
            </FieldBlock>
          );
        }

        if (type === 'bulletChart') {
          return (
            <>
              <FieldPair>
                <FormItem
                  name="bulletChartWidth"
                  label={t('宽度')}
                  rules={integerFieldRules(30, 200)}
                >
                  <InputNumber min={30} max={200} style={{ width: '100%' }} />
                </FormItem>
                <FormItem
                  name="bulletChartHeight"
                  label={t('高度')}
                  rules={integerFieldRules(6, 32)}
                >
                  <InputNumber min={6} max={32} style={{ width: '100%' }} />
                </FormItem>
              </FieldPair>
              <ToggleField
                name="bulletChartShowTarget"
                label={t('显示目标线')}
              />
              <FieldBlock>
                <FormItem name="bulletChartTargetColor" label={t('目标线颜色')}>
                  <ColorPickerControl disableAlpha />
                </FormItem>
              </FieldBlock>
            </>
          );
        }

        if (type === 'button') {
          return (
            <>
              <FieldBlock>
                <FormItem name="buttonStyle" label={t('样式')}>
                  <Select
                    options={[
                      { label: t('实心按钮'), value: 'solid' },
                      { label: t('描边按钮'), value: 'outlined' },
                      { label: t('标签样式'), value: 'tag' },
                    ]}
                  />
                </FormItem>
              </FieldBlock>
              <FieldBlock>
                <FormItem
                  name="buttonBgColor"
                  label={
                    <LabelWithTip
                      label={t('背景颜色')}
                      tooltip={t('按钮背景颜色，留空则使用颜色方案颜色。')}
                    />
                  }
                >
                  <ColorPickerControl disableAlpha />
                </FormItem>
              </FieldBlock>
              <FieldTriple>
                <FormItem name="buttonRadius" label={t('圆角')}>
                  <InputNumber min={0} max={50} style={{ width: '100%' }} />
                </FormItem>
                <FormItem name="buttonPadding" label={t('内边距')}>
                  <InputNumber min={0} max={20} style={{ width: '100%' }} />
                </FormItem>
                <FormItem name="buttonBorderWidth" label={t('边框宽度')}>
                  <InputNumber min={0} max={10} style={{ width: '100%' }} />
                </FormItem>
              </FieldTriple>
            </>
          );
        }

        return null;
      }}
    </FormItem>
  );
}

/** ③ 下标与差值 */
export function SubscriptSectionFields() {
  return (
    <>
      <ToggleField
        name="showSubscript"
        label={
          <LabelWithTip
            label={t('显示下标')}
            tooltip={t(
              '推荐聚合后的数据使用此功能。对于明细类数据，数据量大时可能影响性能，请谨慎使用。启用后会在数值下方显示差值或自定义标识。',
            )}
          />
        }
      />
      <ToggleField
        name="showDifference"
        label={
          <LabelWithTip
            label={t('显示差值')}
            tooltip={t(
              '勾选后，会在数值下方显示实际值与目标值的差值。差值计算方式可自定义（实际值-目标值 或 目标值-实际值）。',
            )}
          />
        }
      />

      <FieldBlock>
        <FormItem
          name="achievedText"
          label={
            <LabelWithTip
              label={t('差值前缀')}
              tooltip={t(
                '当数值满足条件时，显示在差值前面的文本标识，如"达标"、"完成" 箭头↑等。不输入则仅显示差值。',
              )}
            />
          }
        >
          <Input placeholder={t('不输入则仅显示差值')} />
        </FormItem>
      </FieldBlock>

      <FieldPair>
        <FormItem
          name="differenceTargetValue"
          label={
            <LabelWithTip
              label={t('差值目标值')}
              tooltip={t(
                '用于计算差值的目标值，可以是小数或整数。如果不填写，则使用条件判断的目标值。该值仅用于差值计算，不影响颜色判定逻辑。',
              )}
            />
          }
          rules={[{ type: 'number', message: t('请输入有效的数字') }]}
        >
          <InputNumber placeholder={t('可选')} style={{ width: '100%' }} />
        </FormItem>
        <FormItem
          name="differenceCalculation"
          label={
            <LabelWithTip
              label={t('差值计算方式')}
              tooltip={t(
                '部分指标并非越大越好。实际值-目标值适用于越大越好；目标值-实际值适用于越小越好。',
              )}
            />
          }
        >
          <Select
            options={[
              { label: t('实际值 - 目标值'), value: 'actual_minus_target' },
              { label: t('目标值 - 实际值'), value: 'target_minus_actual' },
            ]}
          />
        </FormItem>
      </FieldPair>

      <FieldBlock>
        <FormItem name="differenceColor" label={t('差值颜色')}>
          <ColorPickerControl disableAlpha />
        </FormItem>
      </FieldBlock>
    </>
  );
}
